import { WorkEmpty } from '../../components/work-page.tsx';
import { WorkPage } from '../../components/work-page.tsx';
import '../portal-page.css';
import { useQuery } from '@tanstack/react-query';
import { partnerApi } from '../../api/partner.ts';
import { Alert, LoadingBlock } from '../../components/ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';
import { money, Pill, shortDate, TableShell } from './common.tsx';

/**
 * The customers a partner introduced.
 *
 * What is NOT here is the point: no contact details, no schedule, no clients
 * of theirs, nothing a support agent wrote. A partner has a commercial
 * relationship about these accounts, not an operational one — see the boundary
 * note on `routes/partner.ts`, which is where it is actually enforced.
 */
export default function PartnerCustomersPage() {
  useDocumentTitle('Partner customers');

  const customers = useQuery({
    queryKey: ['partner', 'customers'],
    queryFn: partnerApi.customers,
  });
  // The ledger's own denomination, like every other screen — never the
  // formatter's USD default. The monthly value is the customer's SUBSCRIPTION
  // though, so it is labeled with what the deployment bills in rather than
  // with whatever this partner's commission history happens to be in.
  const currency = customers.data?.currency;
  const subscriptionCurrency = customers.data?.subscriptionCurrency;

  return (
    <div className="portal-page">
      <WorkPage
        title="Customers"
        description="Accounts attributed to your link. A free plan or a trial still counts — it starts earning when it upgrades."
      >
        {customers.isLoading ? <LoadingBlock label="Loading your customers…" /> : null}
        {customers.isError ? (
          <Alert tone="error" title="Your customers could not be loaded">
            {customers.error instanceof Error ? customers.error.message : 'Please try again in a moment.'}
          </Alert>
        ) : null}
        {customers.data && customers.data.customers.length === 0 ? (
          <WorkEmpty title="No customers yet">
            Anyone who signs up through your referral link appears here, usually within a few minutes.
          </WorkEmpty>
        ) : null}
        {customers.data && customers.data.customers.length > 0 ? (
          <TableShell
            label="Attributed customers"
            head={
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Customer
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Status
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Plan
                </th>
                <th scope="col" className="px-4 py-3 text-right font-semibold">
                  Monthly value
                </th>
                <th scope="col" className="px-4 py-3 text-right font-semibold">
                  Eligible revenue
                </th>
                <th scope="col" className="px-4 py-3 text-right font-semibold">
                  Your commission
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Signed up
                </th>
              </tr>
            }
          >
            {customers.data.customers.map((customer) => (
              <tr key={customer.id} className="border-b border-[color:var(--color-line)] last:border-b-0">
                <td className="px-4 py-3">
                  <span className="font-medium">{customer.name}</span>
                  {customer.campaign ? (
                    <div className="text-xs text-[color:var(--color-muted)]">Campaign: {customer.campaign}</div>
                  ) : null}
                </td>
                <td className="px-4 py-3">
                  <Pill value={customer.status} tone={customer.status === 'active' ? 'good' : 'neutral'} />
                </td>
                <td className="px-4 py-3">{customer.plan ?? '—'}</td>
                <td className="px-4 py-3 text-right">{money(customer.recurringValueCents, subscriptionCurrency)}</td>
                <td className="px-4 py-3 text-right">{money(customer.eligibleRevenueCents, currency)}</td>
                <td className="px-4 py-3 text-right font-medium">{money(customer.commissionCents, currency)}</td>
                <td className="px-4 py-3">{shortDate(customer.signedUpAt)}</td>
              </tr>
            ))}
          </TableShell>
        ) : null}
        {customers.data?.truncated ? (
          <p className="mt-3 text-sm text-[color:var(--color-muted)]">
            Showing the {customers.data.customers.length} most recent of {customers.data.total}.
          </p>
        ) : null}
      </WorkPage>
    </div>
  );
}
